import ApplicationSerializer from '@/utilities/serializers/application';
import { ACCOUNT_FLOWS, ANNUAL_INCOME, NETWORTH, ACCOUNT_TYPES } from '@/utilities/constants';
import {
  DEFAULT_AGREEMENTS,
  OPTION_AGREEMENTS,
  MD_AGREEMENTS,
  CRYPTO_AGREEMENTS,
  FOREIGN_TAX_CERTIFICATION,
  DOMESTIC_TAX_CERTIFICATION,
  AGREEMENT_LINKS,
  FUTURES_AGREEMENTS,
  FUTURES_ADDITIONAL_RISK,
} from '@/utilities/agreements';
import { INVALID_COUNTRIES } from '@/utilities/geography';

import Applicant from './applicant.model';
import { useRootStore } from '@/stores/root.store';
import { useUserStore } from '@/stores/user.store';
import { PRIMARY } from '@/services/api/onboarding.service';

export default class Application {
  id = null;
  applicants = [];
  beneficiaries = [];

  stepName = 'account-information';
  maxStepIndex = 0;

  // partner promotions
  promotionCode = undefined;
  platformId = undefined;
  paymentPlanCode = undefined;
  // cache.signedPartnerAgreement;

  // account
  accountKind = 'individual';
  accountType = 'margin';

  // investment profile
  investmentObjective = '';
  riskTolerance = '';
  stockExperience = 0;
  optionExperience = 0;
  totalNetWorthUSD = null; // NETWORTH[0].value
  annualIncomeUSD = ANNUAL_INCOME[0].value;

  // options
  // default to level 1 options
  optionApproval = 1;

  // crypto
  // default crypto to false
  crypto = false;

  // futures
  // default futures to false
  futures = false;
  futuresAccountForm = {
    futuresPlatform: null,
    jointRelationship: null,
    fundsExperience: 0,
    futuresExperience: 0,
    futuresInvestmentObjective: 'SPECULATION', // Default to SPECULATION

    // default these to true since they are mandatory on sign-submit
    ackRiskLossTrading: true,
    ackLeverageTrading: true,
    ackBalanceLosses: true,
    ackDepositRequirement: true,

    isAffiliatedNFA: false,
    isAffiliatedNFADetail: '',
    isFinraNfaMember: false,
    isFinraNfaMemberDetail: '',
    isDormanRelative: false,
    isDormanRelativeDetail: '',
    hasInvestigation: false,
    hasInvestigationDetail: '',
    hasComplaints: false,
    hasComplaintsDetail: '',
    hasOtherAuthority: false,
    hasOtherAuthorityDetail: '',
    hasCommoditiesAccount: false,
    hasCommoditiesAccountDetail: '',
    hasOtherFunds: false,
    hasOtherFundsDetail: '',
    hasSharedLosses: false,
    hasSharedLossesDetail: '',
    isFundedTransfer: false,
    isFundedTransferDetail: '',

    fundingSource: null,
    businessNature: null,
    educationSubject: null, // Only used when businessNature is Education

    iraCustodian: null, // Only used if account is IRA and is set in serializer
  };

  // foreign
  initialContact = null;
  businessNature = null;
  primaryBanking = null;
  expectedWithdrawals = null;
  initialDepositType = null;
  valueInitialDeposit = null;

  // foreign documents
  foreignPrimaryIdUuid = null;
  foreignPrimarySignatureUuid = null;
  foreignSecondaryIdUuid = null;
  foreignSecondarySignatureUuid = null;
  foreignPrimaryAddressVerificationUuid = null;
  foreignSecondaryAddressVerificationUuid = null;

  // custodial
  custodianType = 'UTMA';
  custodialState = undefined;

  // trusted contact
  trustedContactGivenName = undefined;
  trustedContactFamilyName = undefined;
  trustedContactEmailAddress = undefined;
  trustedContactPhoneNumber = undefined;

  // reviewed
  reviewedEntries = false;

  // agreements
  signedAgreements = [];

  // achProfile
  achProfile = null;

  // meta (is sent with the application and stored forever)
  meta = {};

  // cache (is saved on the application ONLY)
  cache = {
    started: true,
  };

  // submitted details
  status = 'NEW';
  verifications = [];

  constructor(attributes = {}) {
    Object.assign(this, attributes);

    if (!attributes.applicants) {
      this.applicants.push(new Applicant());
    }
  }

  get type() {
    if (this.isIra) {
      return this.accountKind;
    }

    if (this.isJoint) {
      return `${this.accountKind}_${this.accountType}_survivor`;
    }

    return `${this.accountKind}_${this.accountType}`;
  }

  set type(value) {
    const types = value.split('_');
    if (value.includes('ira')) {
      this.accountKind = value;
    } else {
      this.accountKind = types[0];
      this.accountType = types[1];
    }
  }

  get riskObjective() {
    return this.riskTolerance && this.investmentObjective ? `${this.riskTolerance}_${this.investmentObjective}` : '';
  }

  set riskObjective(value) {
    const [riskTolerance, investmentObjective] = value.split('_');
    this.riskTolerance = riskTolerance;
    this.investmentObjective = investmentObjective;
  }

  get customerType() {
    // INDIVIDUAL, JOINT, IRA, CUSTODIAN
    if (this.isJoint) return 'JOINT';
    if (this.isIra) return 'IRA';
    if (this.isCustodial) return 'CUSTODIAN';

    return 'INDIVIDUAL';
  }

  get accountDetails() {
    return ACCOUNT_TYPES.find((accountType) => accountType.value === this.accountKind);
  }

  get typeIRA() {
    // TRADITIONAL_IRA, ROLLOVER_ACCOUNT, SEP, ROTH_IRA;
    if (this.accountKind.includes('rollover')) return 'ROLLOVER_ACCOUNT';
    if (this.accountKind.includes('roth')) return 'ROTH_IRA';
    if (this.accountKind.includes('sep')) return 'SEP';

    return this.isIra ? 'TRADITIONAL_IRA' : undefined;
  }

  get investmentExperience() {
    const EXPERIENCE_OPTIONS = ['NONE', 'LIMITED', 'GOOD', 'EXTENSIVE'];
    const stock = this.stockExperience;
    const option = this.optionExperience;

    const average = Math.floor((stock + option) / 2);

    return EXPERIENCE_OPTIONS[average];
  }

  set investmentExperience(value) {}

  get options() {
    return this.optionApproval > 0;
  }

  set options(value) {}

  get professional() {
    return !this.primaryApplicant?.nonProfessional;
  }

  set professional(value) {}

  get primaryApplicant() {
    return this.applicants[0];
  }

  get secondaryApplicant() {
    if (this.applicants.length === 2) {
      return this.applicants[1];
    }

    const _secondaryApplicant = new Applicant({
      permanentResident: this.primaryApplicant.permanentResident,
      citizenshipCountry: this.primaryApplicant.citizenshipCountry,
      cache: {
        residentCountry: this.primaryApplicant.residencyCountry,
        isSecondaryApplicant: true,
      },
    });
    this.applicants.push(_secondaryApplicant);

    return _secondaryApplicant;
  }

  get hasSecondaryApplicant() {
    return this.applicants.length === 2;
  }

  get formKey() {
    if (this.isIra) return 'newAccountIraForm';
    if (this.isForeign) return 'newAccountForeignForm';
    return 'newAccountForm';
  }

  get steps() {
    const steps = ACCOUNT_FLOWS[this.type];
    const insertIndex = steps.indexOf('funding-setup');

    // foreign but foreign step not present
    if (this.isForeign && !steps.includes('foreign-diligence')) {
      steps.splice(insertIndex, 0, 'foreign-diligence');
    }
    // not foreign, but foreign step present
    if (!this.isForeign && steps.includes('foreign-diligence')) {
      steps.splice(steps.indexOf('foreign-diligence'), 1);
    }

    // not ppc, but ppc step present
    if (!this.hasValidPaymentPlan && steps.includes('partner-information')) {
      steps.splice(steps.indexOf('partner-information'), 1);
    }
    // ppc but ppc step not present
    if (this.hasValidPaymentPlan && !steps.includes('partner-information')) {
      steps.splice(1, 0, 'partner-information');
    }

    // not futures, but futures step present
    if (!this.hasFutures && steps.includes('futures-information')) {
      steps.splice(steps.indexOf('futures-information'), 1);
    }
    // futures but futures step not present
    if (this.hasFutures && !steps.includes('futures-information')) {
      steps.splice(steps.indexOf('investment-information') + 1, 0, 'futures-information');
    }

    return steps;
  }

  get stepIndex() {
    return this.steps.indexOf(this.stepName);
  }

  get displayResidentCountry() {
    const isForeign = this.primaryApplicant.citizenshipCountry !== 'USA';
    const isNotPermanentResident = !this.primaryApplicant.permanentResident;
    const isValidCountry =
      !INVALID_COUNTRIES.includes(this.primaryApplicant.citizenshipCountry) || this.primaryApplicant.permanentResident;

    return isForeign && isNotPermanentResident && isValidCountry;
  }

  get hasValidPaymentPlan() {
    const hasPaymentPlan = this.paymentPlanCode || this.cache.ppc;
    const hasPaymentPlanCode = this.paymentPlanCode !== '' && this.cache.ppc?.paymentPlanCode !== '';
    return hasPaymentPlan && hasPaymentPlanCode;
  }

  get hasNextStep() {
    return this.stepIndex < this.steps.length - 1;
  }

  get hasPreviousStep() {
    return this.stepIndex > 0;
  }

  get hasOptions() {
    return this.options;
  }

  get hasCrypto() {
    return this.crypto;
  }

  get hasFutures() {
    return this.futures;
  }

  get isCustodial() {
    return this.accountKind.includes('custodial');
  }

  get isIra() {
    return this.accountKind.includes('ira');
  }

  get isJoint() {
    return this.accountKind.includes('joint');
  }

  get isForeign() {
    return this.primaryApplicant.isForeign;
  }

  get isMargin() {
    return this.accountType === 'margin';
  }

  get isInProgress() {
    return this.status === 'NEW' || this.isStarted;
  }

  get isSubmitted() {
    const STATUSES = ['SUBMITTED', 'VERIFIED', 'PENDING', 'APPROVED'];
    return STATUSES.includes(this.status);
  }

  get isDone() {
    const STATUSES = ['REJECTED', 'CANCELED', 'COMPLETED'];
    return STATUSES.includes(this.status);
  }

  get isStarted() {
    return this.cache?.started === true;
  }

  get isCompleted() {
    return this.status === 'COMPLETED';
  }

  get isRejected() {
    return this.status === 'REJECTED';
  }

  get hasAchProfile() {
    return this.achProfile?.plaidToken;
  }

  get agreementLinks() {
    const links = AGREEMENT_LINKS;

    AGREEMENT_LINKS.subscription_agreement.url = this.cache?.ppc?.agreementLink;
    AGREEMENT_LINKS.subscription_agreement.displayName = this.cache?.ppc?.agreementName;

    return links;
  }

  // minimal required agreements
  get requiredAgreements() {
    let agreements = DEFAULT_AGREEMENTS[this.type];
    if (this.cache.ppc?.agreementLink && !agreements.includes('subscription_agreement')) {
      agreements.push('subscription_agreement');
    }

    if (this.hasOptions) {
      agreements = agreements.concat(OPTION_AGREEMENTS);
    }

    // domestic account gets w9
    if (!this.isForeign) {
      agreements = agreements.concat([DOMESTIC_TAX_CERTIFICATION]);
    }
    // foreign account gets w8-ben
    if (this.isForeign) {
      agreements = agreements.concat([FOREIGN_TAX_CERTIFICATION]);
    }

    return agreements.sort();
  }

  // actual agreements signed by this online application
  get pendingAgreements() {
    // get the default agreements
    let agreements = this.requiredAgreements;

    if (this.crypto) {
      agreements = agreements.concat(CRYPTO_AGREEMENTS);
    }

    if (this.futures) {
      agreements = agreements.concat(FUTURES_AGREEMENTS);

      // requires additional futures risk disclosure
      if (this.requiresAdditionalFuturesRiskDisclosure) {
        agreements = agreements.concat(FUTURES_ADDITIONAL_RISK);
      }
    }

    // if professional, do not add market data agreements
    if (this.professional) {
      return agreements;
    }

    // must be non-professional, add the market data agreements
    agreements = agreements.concat(MD_AGREEMENTS);

    return agreements;
  }

  get futuresAgreements() {
    let agreements = [...FUTURES_AGREEMENTS];
    if (this.requiresAdditionalFuturesRiskDisclosure) {
      agreements = agreements.concat(FUTURES_ADDITIONAL_RISK);
    }

    return agreements;
  }

  get primaryBeneficiaries() {
    return this.beneficiaries.filter((item) => item.isPrimary);
  }

  get contingentBeneficiaries() {
    return this.beneficiaries.filter((item) => item.isContingent);
  }

  get totalPrimaryPercentage() {
    return this.primaryBeneficiaries.reduce((total, current) => {
      return total + parseInt(current.sharePercent);
    }, 0);
  }

  get totalContingentPercentage() {
    return this.contingentBeneficiaries.reduce((total, current) => {
      return total + parseInt(current.sharePercent);
    }, 0);
  }

  get hasDuplicateBeneficiaries() {
    if (!this.isIra) return false;
    const allSsn = this.beneficiaries.map((b) => b.socialSecurityNumber);
    return allSsn.length > [...new Set(allSsn)].length;
  }

  get referredToBroker() {
    return !!this.platformId;
  }

  get requiresAdditionalFuturesRiskDisclosure() {
    const primaryApplicantAge = this.primaryApplicant.age < 21 || this.primaryApplicant.age > 65;
    const zeroFuturesExperience = this.futuresAccountForm.futuresExperience === 0;
    const lowAnnualIncome = this.annualIncomeUSD?.max === ANNUAL_INCOME[0].value.max;
    const lowNetworth = this.totalNetWorthUSD?.max === NETWORTH[0].value.max;
    const isIra = this.isIra;

    return primaryApplicantAge || zeroFuturesExperience || lowAnnualIncome || lowNetworth || isIra;
  }

  nextStep() {
    if (this.hasNextStep) {
      this.stepName = this.steps[this.stepIndex + 1];
      this.maxStepIndex = this.stepIndex;
    }
  }

  previousStep() {
    if (this.hasPreviousStep) {
      this.stepName = this.steps[this.stepIndex - 1];
    }
  }

  isPreviousStep(step) {
    return this.stepIndex > this.steps.indexOf(step);
  }

  delete() {
    try {
      return useRootStore().onboardingService.deleteApplication(this.id);
    } catch (e) {
      console.error(e);
    }
  }

  save() {
    try {
      this.maxStepIndex = this.stepIndex;
      const data = ApplicationSerializer.serialize(this);
      return useRootStore().onboardingService.updateApplication(this.id, data);
    } catch (e) {
      console.error(e);
      // Sentry.captureException(e);
    }
  }

  async submit() {
    // get signed agreements list
    this.agreements = this.pendingAgreements;

    // TODO: revert this as it should be done on the backend
    this.agreements = this.agreements.map((a) => {
      return { type: a, timestamp: new Date().toISOString() };
    });

    try {
      // save the application
      await this.save();

      // submit the application
      return useRootStore().onboardingService.submitApplication(this.id);
    } catch (e) {
      console.error(e);
      // Sentry.captureException(e);
    }
  }

  async fetchPPC() {
    try {
      const response = await useRootStore().onboardingService.fetchPaymentPlans(this.platformId, this.promotionCode);
      // save the ppc on the application
      this.paymentPlanCode = response.data.paymentPlanCode;
      // store all ppc data, for later use
      this.cache.ppc = response.data;
    } catch (e) {
      if (e?.response?.status === 404) {
        // reset the paymentPlanCode
        this.paymentPlanCode = null;
        this.cache.ppc = null;

        return;
      }
    }
  }

  removeSecondaryApplicant() {
    if (this.hasSecondaryApplicant) this.applicants.pop();
  }

  async removeSecondaryApplicantFiles() {
    if (this.foreignSecondaryIdUuid) {
      await useRootStore().onboardingService.removeImage(this.id, this.foreignSecondaryIdUuid);
      this.foreignSecondaryIdUuid = null;
    }
    if (this.foreignSecondarySignatureUuid) {
      await useRootStore().onboardingService.removeImage(this.id, this.foreignSecondarySignatureUuid);
      this.foreignSecondarySignatureUuid = null;
    }
    if (this.foreignSecondaryAddressVerificationUuid) {
      await useRootStore().onboardingService.removeImage(this.id, this.foreignSecondaryAddressVerificationUuid);
      this.foreignSecondaryAddressVerificationUuid = null;
    }
  }

  get isBrokerDealer() {
    return this.businessNature === 'Stock, Option, or Commodities Broker Dealer or Exchange';
  }

  get trustedContactMatchesPrimaryApplicant() {
    return (
      this.primaryApplicant.firstName.trim().toLowerCase() === this.trustedContactGivenName?.trim().toLowerCase() &&
      this.primaryApplicant.lastName.trim().toLowerCase() === this.trustedContactFamilyName?.trim().toLowerCase()
    );
  }

  get applicantNames() {
    return this.applicants
      ?.map((applicant) => {
        return [applicant.firstName, applicant.lastName].join(' ');
      })
      .join(', ');
  }

  get actionRequired() {
    if (
      ['SUBMITTED', 'VERIFIED', 'PENDING'].includes(this.status) &&
      (this.primaryApplicant.actionRequired || this.secondaryApplicant?.actionRequired)
    ) {
      return true;
    }
    return false;
  }

  uploadIdImage(file, applicant = PRIMARY) {
    return useRootStore().onboardingService.uploadIdImage(this.id, file, applicant);
  }

  uploadSignatureImage(file, applicant = PRIMARY) {
    return useRootStore().onboardingService.uploadSignatureImage(this.id, file, applicant);
  }

  uploadAddressVerificationImage(file, applicant = PRIMARY) {
    return useRootStore().onboardingService.uploadAddressVerificationImage(this.id, file, applicant);
  }

  removeImage(uuid) {
    return useRootStore().onboardingService.removeImage(this.id, uuid);
  }

  citizenshipChange() {
    this.accountKind = 'individual';
    this.accountType = 'margin';
  }

  kindChange() {
    const userStore = useUserStore();
    this.accountType = this.accountDetails.defaultType;
    this.typeChange();

    this.primaryApplicant.firstName = userStore.user.firstName;
    this.primaryApplicant.lastName = userStore.user.lastName;
    this.removeSecondaryApplicantFiles();
    this.removeSecondaryApplicant();

    // If application is custodial, empty out the primary applicant name details
    // Then set the secondaryApplicant info to be the info that the user signed up with originally
    if (this.isCustodial && this.secondaryApplicant) {
      this.primaryApplicant.prefix = '';
      this.primaryApplicant.firstName = '';
      this.primaryApplicant.lastName = '';
      this.primaryApplicant.suffix = '';

      this.secondaryApplicant.firstName = userStore.user.firstName;
      this.secondaryApplicant.lastName = userStore.user.lastName;
      this.secondaryApplicant.email = userStore.user.email;
    }
  }

  typeChange() {
    this.optionApproval = 1;
    this.maxStepIndex = 0;
  }

  optionChange() {
    this.maxStepIndex = 0;
  }

  async mixpanelUserProperties() {
    const { data } = await useRootStore().onboardingService.fetchPlatforms();
    const platform = data.find((el) => el.id === parseInt(this.platformId || '')) || {};

    const properties = {
      'Platform Name': platform.name,
      'Promotion code': this.promotionCode,
      Foreign: this.isForeign,
      Professional: this.professional,
      PPC: this.paymentPlanCode,
    };

    return properties;
  }
}
